import {PaymentTypeText} from '../../features/auth/types';
import {IdentitySettings} from '../types';
import {api} from './api';

export const identitiesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    anonymize: builder.mutation({
      query: () => ({url: '/identities/anonymize', method: 'POST'}),
    }),

    changeAvatar: builder.mutation<null, {avatarImageBase64: string}>({
      query: ({avatarImageBase64}) => ({url: `/identities/avatars`, method: 'POST', body: {avatarImageBase64}}),
      invalidatesTags: ['Avatar'],
    }),

    changePassword: builder.mutation<
      any,
      {confirmPassword: string; newPassword: string; otp: string; usernameOrEmail: string}
    >({
      query: (arg) => ({
        url: '/identities/change-password',
        method: 'POST',
        body: arg,
      }),
    }),

    getAvatar: builder.query<{avatarImageBase64: string}, void>({
      query: () => ({url: '/identities/avatars', method: 'GET'}),
      providesTags: ['Avatar'],
    }),

    getIdentitySettings: builder.query<IdentitySettings, void>({
      query: () => ({
        url: `/identities/settings`,
        method: 'GET',
      }),
      providesTags: ['IdentitySettings'],
    }),

    getVoucher: builder.query<
      {paymentType: PaymentTypeText; subscriptionFee: number; subscriptionType: number},
      {voucherCode: string}
    >({
      query: ({voucherCode}) => ({url: `/identities/retrieve-voucher?voucherCode=${voucherCode}`, method: 'GET'}),
    }),

    login: builder.mutation<{expiration: string; token: string; userId: number}, {password: string; username: string}>({
      query: (arg) => ({
        url: '/identities/login',
        method: 'POST',
        body: arg,
      }),
    }),

    loginWithToken: builder.mutation<{expiration: string; token: string; userId: number}, {token: string}>({
      query: (arg) => ({
        url: '/identities/login-with-token',
        method: 'POST',
        body: arg,
      }),
    }),

    modifyName: builder.mutation<null, {name: string}>({
      query: ({name}) => ({
        url: '/identities/modify-name',
        method: 'POST',
        body: {name},
      }),
    }),

    modifyPassword: builder.mutation<unknown, {currentPassword?: string; password: string; confirmPassword: string}>({
      query: (arg) => ({
        url: '/identities/modify-password',
        method: 'POST',
        body: arg,
      }),
    }),

    refreshToken: builder.mutation<{token: string}, void>({
      query: () => ({
        url: `/identities/refresh`,
        method: 'POST',
      }),
    }),

    register: builder.mutation<
      void,
      {confirmPassword: string; email: string; name: string; newPassword: string; username: string}
    >({
      query: (arg) => ({
        url: '/identities/register',
        method: 'POST',
        body: arg,
      }),
    }),

    registerResendOTP: builder.mutation<unknown, {usernameOrEmail: string}>({
      query: (arg) => ({
        url: '/identities/register/resend-otp',
        method: 'POST',
        body: arg,
      }),
    }),

    registerVerifyOTP: builder.mutation<{token: string}, {otp: string; usernameOrEmail: string}>({
      query: (arg) => ({
        url: '/identities/register/verify-otp',
        method: 'POST',
        body: arg,
      }),
    }),

    resetPassword: builder.mutation<unknown, {usernameOrEmail: string}>({
      query: (arg) => ({
        url: '/identities/reset-password',
        method: 'POST',
        body: arg,
      }),
    }),

    resetPasswordResendOTP: builder.mutation<unknown, {usernameOrEmail: string}>({
      query: (arg) => ({
        url: '/identities/reset-password/resend-otp',
        method: 'POST',
        body: arg,
      }),
    }),

    redeemVoucher: builder.mutation<null, {voucherCode: string}>({
      query: ({voucherCode}) => ({url: `/identities/redeem-voucher`, method: 'POST', body: {voucherCode}}),
    }),

    sendFeedback: builder.mutation<null, {feedbackType: number; message: string; screenshot: string}>({
      query: (arg) => ({
        url: `/identities/feedbacks`,
        method: 'POST',
        body: arg,
      }),
    }),

    setIdentitySettings: builder.mutation<IdentitySettings, IdentitySettings>({
      query: (arg) => ({
        url: `/identities/settings`,
        method: 'PUT',
        body: arg,
      }),
      onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
        const patchResult = dispatch(
          identitiesApi.util.updateQueryData('getIdentitySettings', undefined, (identitySettings) => {
            Object.assign(identitySettings, arg);
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ['Biotwins'],
    }),

    startDemo: builder.mutation<{expiration: string; token: string; userId: number}, void>({
      query: () => ({
        url: '/identities/demo/start',
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useLoginWithTokenMutation,

  useAnonymizeMutation,
  useChangeAvatarMutation,
  useChangePasswordMutation,
  useGetAvatarQuery,
  useLazyGetVoucherQuery,
  useModifyNameMutation,
  useModifyPasswordMutation,
  useRedeemVoucherMutation,
  useRefreshTokenMutation,
  useSendFeedbackMutation,

  useRegisterMutation,
  useRegisterResendOTPMutation,
  useRegisterVerifyOTPMutation,

  useResetPasswordMutation,
  useResetPasswordResendOTPMutation,

  useGetIdentitySettingsQuery,
  useSetIdentitySettingsMutation,

  useStartDemoMutation,
} = identitiesApi;
